'use client';
import React, { useState } from 'react';
import { FileText, Download, Filter, Search, RefreshCw, CheckCircle, AlertTriangle, Clock, Users, BarChart3, Shield, Globe, FileSpreadsheet, FileBadge, Calendar, Eye, Archive, Hash, Plus, X, Mail, Repeat, ChevronDown, Play, Pause, Trash2, CheckSquare, XCircle, Timer } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import Icon from '@/components/ui/AppIcon';




type ExportFormat = 'pdf' | 'excel' | 'csv' | 'xml';
type ReportType = 'investor-register' | 'kyc-aml' | 'campaign-performance' | 'fatca-crs';
type ActiveTab = ReportType | 'scheduled-exports';

interface ExportJob {
  id: string;
  reportType: string;
  format: ExportFormat;
  requestedBy: string;
  requestedAt: string;
  status: 'completed' | 'processing' | 'failed';
  fileSize?: string;
  recordCount?: number;
}

interface ScheduledJob {
  id: string;
  name: string;
  reportType: string;
  formats: ExportFormat[];
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  dayOfWeek?: string;
  dayOfMonth?: number;
  time: string;
  recipients: string[];
  status: 'active' | 'paused';
  createdBy: string;
  createdAt: string;
  nextRun: string;
  lastRun?: string;
}

interface JobExecution {
  id: string;
  jobId: string;
  jobName: string;
  reportType: string;
  formats: ExportFormat[];
  startedAt: string;
  completedAt?: string;
  status: 'completed' | 'failed' | 'running';
  recordCount?: number;
  fileSize?: string;
  recipients: number;
  errorMessage?: string;
  duration?: string;
}

const investorRegisterData = [
  { id: 'INV-001847', name: 'Ashbury Pension Trust', type: 'Institutional', jurisdiction: 'United Kingdom', taxId: 'GB-PT-884721', accreditation: 'Verified', aum: '$4,200,000', investedDate: '2025-03-14', fatcaStatus: 'Compliant', crsStatus: 'Reported', kycStatus: 'Approved' },
  { id: 'INV-002341', name: 'Priya Narasimhan', type: 'Individual', jurisdiction: 'India', taxId: 'IN-PAN-ABCDE1234F', accreditation: 'Verified', aum: '$180,000', investedDate: '2026-01-08', fatcaStatus: 'Exempt', crsStatus: 'Reported', kycStatus: 'Approved' },
  { id: 'INV-003012', name: 'Thornfield Holdings LLC', type: 'Corporate', jurisdiction: 'Cayman Islands', taxId: 'KY-CORP-30124', accreditation: 'Verified', aum: '$12,500,000', investedDate: '2024-11-22', fatcaStatus: 'Compliant', crsStatus: 'Pending', kycStatus: 'EDD Required' },
  { id: 'INV-003847', name: 'Meridian Family Office', type: 'Family Office', jurisdiction: 'Switzerland', taxId: 'CH-FO-847291', accreditation: 'Verified', aum: '$28,000,000', investedDate: '2024-08-05', fatcaStatus: 'Compliant', crsStatus: 'Reported', kycStatus: 'Approved' },
  { id: 'INV-004122', name: 'Crestwood Capital Partners', type: 'Fund', jurisdiction: 'Luxembourg', taxId: 'LU-FUND-412298', accreditation: 'Verified', aum: '$67,400,000', investedDate: '2023-06-17', fatcaStatus: 'Compliant', crsStatus: 'Reported', kycStatus: 'Approved' },
  { id: 'INV-004891', name: 'Haruto Yamamoto', type: 'Individual', jurisdiction: 'Japan', taxId: 'JP-MY-884921', accreditation: 'Pending', aum: '$95,000', investedDate: '2026-07-30', fatcaStatus: 'Exempt', crsStatus: 'Pending', kycStatus: 'Under Review' },
  { id: 'INV-005234', name: 'Vantage Sovereign Fund', type: 'Sovereign', jurisdiction: 'UAE', taxId: 'AE-SOV-523401', accreditation: 'Verified', aum: '$150,000,000', investedDate: '2022-12-01', fatcaStatus: 'Exempt', crsStatus: 'Exempt', kycStatus: 'Approved' },
];

const kycAmlData = [
  { id: 'KYC-2026-0441', investor: 'Priya Narasimhan', type: 'Individual', submittedDate: '2026-08-01', reviewedDate: '2026-08-20', status: 'Approved', riskScore: 18, riskBand: 'Low', amlFlags: 0, pepMatch: false, sanctionsMatch: false, eddRequired: false, reviewer: 'L. Okafor', nextReview: '2027-08-01' },
  { id: 'KYC-2026-0389', investor: 'Thornfield Holdings LLC', type: 'Corporate', submittedDate: '2026-07-15', reviewedDate: null, status: 'EDD Required', riskScore: 74, riskBand: 'High', amlFlags: 2, pepMatch: false, sanctionsMatch: false, eddRequired: true, reviewer: 'M. Patel', nextReview: null },
  { id: 'KYC-2026-0312', investor: 'Ashbury Pension Trust', type: 'Institutional', submittedDate: '2025-03-01', reviewedDate: '2025-03-14', status: 'Approved', riskScore: 12, riskBand: 'Low', amlFlags: 0, pepMatch: false, sanctionsMatch: false, eddRequired: false, reviewer: 'L. Okafor', nextReview: '2026-03-14' },
  { id: 'KYC-2026-0278', investor: 'Haruto Yamamoto', type: 'Individual', submittedDate: '2026-07-30', reviewedDate: null, status: 'Under Review', riskScore: 31, riskBand: 'Medium', amlFlags: 1, pepMatch: false, sanctionsMatch: false, eddRequired: false, reviewer: 'Unassigned', nextReview: null },
  { id: 'KYC-2025-0891', investor: 'Crestwood Capital Partners', type: 'Fund', submittedDate: '2023-06-01', reviewedDate: '2023-06-17', status: 'Approved', riskScore: 22, riskBand: 'Low', amlFlags: 0, pepMatch: false, sanctionsMatch: false, eddRequired: false, reviewer: 'A. Voss', nextReview: '2026-06-17' },
  { id: 'KYC-2026-0501', investor: 'Meridian Family Office', type: 'Family Office', submittedDate: '2024-07-20', reviewedDate: '2024-08-05', status: 'Approved', riskScore: 28, riskBand: 'Low', amlFlags: 0, pepMatch: false, sanctionsMatch: false, eddRequired: false, reviewer: 'L. Okafor', nextReview: '2025-08-05' },
];

const campaignPerformanceData = [
  { id: 'CAMP-2024-0012', name: 'Thornfield RE Fund III', issuer: 'Thornfield Capital', type: 'Real Estate', targetRaise: '$25,000,000', raisedAmount: '$22,400,000', completion: 89.6, investors: 142, status: 'Funded', launchDate: '2024-09-01', closeDate: '2025-03-31', irr: '14.2%', distributions: '$1,240,000' },
  { id: 'CAMP-2025-0034', name: 'Vantage Credit Fund I', issuer: 'Vantage Private Markets', type: 'Private Credit', targetRaise: '$50,000,000', raisedAmount: '$38,700,000', completion: 77.4, investors: 89, status: 'Open', launchDate: '2025-06-15', closeDate: '2026-12-31', irr: 'N/A', distributions: '$0' },
  { id: 'CAMP-2025-0067', name: 'Meridian Growth REIT', issuer: 'Meridian Capital', type: 'REIT', targetRaise: '$100,000,000', raisedAmount: '$0', completion: 0, investors: 0, status: 'Compliance Hold', launchDate: null, closeDate: null, irr: 'N/A', distributions: '$0' },
  { id: 'CAMP-2023-0008', name: 'Crestwood Infrastructure I', issuer: 'Crestwood Alternatives', type: 'Infrastructure', targetRaise: '$75,000,000', raisedAmount: '$75,000,000', completion: 100, investors: 312, status: 'Closed', launchDate: '2023-01-10', closeDate: '2023-12-31', irr: '11.8%', distributions: '$8,900,000' },
  { id: 'CAMP-2026-0091', name: 'Ashford Tech Ventures II', issuer: 'Ashford Wealth', type: 'Venture Capital', targetRaise: '$30,000,000', raisedAmount: '$12,100,000', completion: 40.3, investors: 54, status: 'Open', launchDate: '2026-04-01', closeDate: '2027-03-31', irr: 'N/A', distributions: '$0' },
];

const fatcaCrsData = [
  { id: 'FATCA-2026-001', investor: 'Ashbury Pension Trust', tin: 'GB-PT-884721', country: 'United Kingdom', accountType: 'Depository', balance: '$4,200,000', reportingYear: 2025, fatcaStatus: 'Compliant', crsStatus: 'Reported', filingDate: '2026-06-30', xmlGenerated: true },
  { id: 'FATCA-2026-002', investor: 'Crestwood Capital Partners', tin: 'LU-FUND-412298', country: 'Luxembourg', accountType: 'Custodial', balance: '$67,400,000', reportingYear: 2025, fatcaStatus: 'Compliant', crsStatus: 'Reported', filingDate: '2026-06-30', xmlGenerated: true },
  { id: 'FATCA-2026-003', investor: 'Meridian Family Office', tin: 'CH-FO-847291', country: 'Switzerland', accountType: 'Other', balance: '$28,000,000', reportingYear: 2025, fatcaStatus: 'Compliant', crsStatus: 'Reported', filingDate: '2026-06-30', xmlGenerated: true },
  { id: 'FATCA-2026-004', investor: 'Thornfield Holdings LLC', tin: 'KY-CORP-30124', country: 'Cayman Islands', accountType: 'Custodial', balance: '$12,500,000', reportingYear: 2025, fatcaStatus: 'Compliant', crsStatus: 'Pending', filingDate: null, xmlGenerated: false },
  { id: 'FATCA-2026-005', investor: 'Priya Narasimhan', tin: 'IN-PAN-ABCDE1234F', country: 'India', accountType: 'Depository', balance: '$180,000', reportingYear: 2025, fatcaStatus: 'Exempt', crsStatus: 'Reported', filingDate: '2026-06-30', xmlGenerated: true },
  { id: 'FATCA-2026-006', investor: 'Vantage Sovereign Fund', tin: 'AE-SOV-523401', country: 'UAE', accountType: 'Other', balance: '$150,000,000', reportingYear: 2025, fatcaStatus: 'Exempt', crsStatus: 'Exempt', filingDate: '2026-06-30', xmlGenerated: true },
];

const recentExports: ExportJob[] = [
  { id: 'EXP-001', reportType: 'Investor Register', format: 'excel', requestedBy: 'A. Voss', requestedAt: '2026-08-20 16:42', status: 'completed', fileSize: '284 KB', recordCount: 3847 },
  { id: 'EXP-002', reportType: 'FATCA/CRS XML', format: 'xml', requestedBy: 'L. Okafor', requestedAt: '2026-08-20 15:18', status: 'completed', fileSize: '1.2 MB', recordCount: 3241 },
  { id: 'EXP-003', reportType: 'KYC/AML Status', format: 'pdf', requestedBy: 'M. Patel', requestedAt: '2026-08-20 14:05', status: 'completed', fileSize: '892 KB', recordCount: 3847 },
  { id: 'EXP-004', reportType: 'Campaign Performance', format: 'csv', requestedBy: 'A. Voss', requestedAt: '2026-08-20 11:30', status: 'completed', fileSize: '48 KB', recordCount: 11 },
  { id: 'EXP-005', reportType: 'Investor Register', format: 'pdf', requestedBy: 'System', requestedAt: '2026-08-20 08:00', status: 'completed', fileSize: '3.4 MB', recordCount: 3847 },
];

const initialScheduledJobs: ScheduledJob[] = [
  { id: 'SCHED-001', name: 'Daily KYC/AML Status', reportType: 'KYC / AML Status', formats: ['pdf', 'excel'], frequency: 'daily', time: '07:00', recipients: ['compliance@crowdvault.com', 'l.okafor@crowdvault.com'], status: 'active', createdBy: 'L. Okafor', createdAt: '2026-07-01', nextRun: '2026-08-21 07:00', lastRun: '2026-08-20 07:00' },
  { id: 'SCHED-002', name: 'Weekly Investor Register', reportType: 'Investor Register', formats: ['excel'], frequency: 'weekly', dayOfWeek: 'Monday', time: '06:00', recipients: ['regops@crowdvault.com', 'a.voss@crowdvault.com'], status: 'active', createdBy: 'A. Voss', createdAt: '2026-06-15', nextRun: '2026-08-24 06:00', lastRun: '2026-08-17 06:00' },
  { id: 'SCHED-003', name: 'Monthly FATCA/CRS Bundle', reportType: 'FATCA / CRS XML', formats: ['xml', 'pdf'], frequency: 'monthly', dayOfMonth: 1, time: '05:00', recipients: ['tax@crowdvault.com', 'external-auditor@kpmg.com'], status: 'active', createdBy: 'M. Patel', createdAt: '2026-01-01', nextRun: '2026-09-01 05:00', lastRun: '2026-08-01 05:00' },
  { id: 'SCHED-004', name: 'Quarterly Campaign Performance', reportType: 'Campaign Performance', formats: ['pdf', 'csv'], frequency: 'quarterly', dayOfMonth: 1, time: '08:00', recipients: ['board@crowdvault.com', 'cfo@crowdvault.com'], status: 'paused', createdBy: 'A. Voss', createdAt: '2026-01-15', nextRun: '2026-10-01 08:00', lastRun: '2026-07-01 08:00' },
];

const initialExecutionHistory: JobExecution[] = [
  { id: 'EXEC-0041', jobId: 'SCHED-001', jobName: 'Daily KYC/AML Status', reportType: 'KYC / AML Status', formats: ['pdf', 'excel'], startedAt: '2026-08-20 07:00:00', completedAt: '2026-08-20 07:01:14', status: 'completed', recordCount: 3847, fileSize: '1.1 MB', recipients: 2, duration: '1m 14s' },
  { id: 'EXEC-0040', jobId: 'SCHED-002', jobName: 'Weekly Investor Register', reportType: 'Investor Register', formats: ['excel'], startedAt: '2026-08-17 06:00:00', completedAt: '2026-08-17 06:02:31', status: 'completed', recordCount: 3847, fileSize: '284 KB', recipients: 2, duration: '2m 31s' },
  { id: 'EXEC-0039', jobId: 'SCHED-003', jobName: 'Monthly FATCA/CRS Bundle', reportType: 'FATCA / CRS XML', formats: ['xml', 'pdf'], startedAt: '2026-08-01 05:00:00', completedAt: '2026-08-01 05:04:52', status: 'completed', recordCount: 3241, fileSize: '2.8 MB', recipients: 2, duration: '4m 52s' },
  { id: 'EXEC-0038', jobId: 'SCHED-001', jobName: 'Daily KYC/AML Status', reportType: 'KYC / AML Status', formats: ['pdf', 'excel'], startedAt: '2026-08-19 07:00:00', completedAt: '2026-08-19 07:00:48', status: 'completed', recordCount: 3847, fileSize: '1.1 MB', recipients: 2, duration: '48s' },
  { id: 'EXEC-0037', jobId: 'SCHED-001', jobName: 'Daily KYC/AML Status', reportType: 'KYC / AML Status', formats: ['pdf', 'excel'], startedAt: '2026-08-18 07:00:00', completedAt: undefined, status: 'failed', recipients: 0, errorMessage: 'SMTP relay timeout — recipients not delivered. Retry scheduled.', duration: '30s' },
  { id: 'EXEC-0036', jobId: 'SCHED-004', jobName: 'Quarterly Campaign Performance', reportType: 'Campaign Performance', formats: ['pdf', 'csv'], startedAt: '2026-07-01 08:00:00', completedAt: '2026-07-01 08:01:09', status: 'completed', recordCount: 11, fileSize: '52 KB', recipients: 2, duration: '1m 9s' },
  { id: 'EXEC-0035', jobId: 'SCHED-002', jobName: 'Weekly Investor Register', reportType: 'Investor Register', formats: ['excel'], startedAt: '2026-08-10 06:00:00', completedAt: undefined, status: 'failed', recipients: 0, errorMessage: 'Database connection pool exhausted. Job aborted after 30s timeout.', duration: '30s' },
];

const formatIcons: Record<ExportFormat, React.ElementType> = {
  pdf: FileBadge,
  excel: FileSpreadsheet,
  csv: FileText,
  xml: Hash,
};

const formatColors: Record<ExportFormat, string> = {
  pdf: 'text-critical',
  excel: 'text-positive',
  csv: 'text-info',
  xml: 'text-accent',
};

function ExportButton({ format, onClick }: { format: ExportFormat; onClick: () => void }) {
  const FmtIcon = formatIcons[format];
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all hover:opacity-80 ${
        format === 'pdf' ? 'border-critical/30 bg-critical/5 text-critical' :
        format === 'excel' ? 'border-positive/30 bg-positive/5 text-positive' :
        format === 'csv'? 'border-info/30 bg-info/5 text-info' : 'border-accent/30 bg-accent/5 text-accent'
      }`}
    >
      <FmtIcon size={12} />
      {format.toUpperCase()}
    </button>
  );
}

function RiskBandPill({ band }: { band: string }) {
  const colors: Record<string, string> = {
    Low: 'bg-positive/10 text-positive border-positive/20',
    Medium: 'bg-warning/10 text-warning border-warning/20',
    High: 'bg-critical/10 text-critical border-critical/20',
    Critical: 'bg-critical/20 text-critical border-critical/30',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${colors[band] ?? 'bg-surface-4 text-muted-foreground border-border'}`}>
      {band}
    </span>
  );
}

function FormatPill({ format }: { format: ExportFormat }) {
  const FmtIcon = formatIcons[format];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded ${
      format === 'pdf' ? 'bg-critical/10 text-critical' :
      format === 'excel' ? 'bg-positive/10 text-positive' :
      format === 'csv' ? 'bg-info/10 text-info' : 'bg-accent/10 text-accent'
    }`}>
      <FmtIcon size={10} />{format.toUpperCase()}
    </span>
  );
}

interface CreateJobModalProps {
  onClose: () => void;
  onSave: (job: ScheduledJob) => void;
}

function CreateJobModal({ onClose, onSave }: CreateJobModalProps) {
  const [name, setName] = useState('');
  const [reportType, setReportType] = useState('Investor Register');
  const [formats, setFormats] = useState<ExportFormat[]>(['pdf']);
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [time, setTime] = useState('07:00');
  const [recipientInput, setRecipientInput] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);

  const toggleFormat = (fmt: ExportFormat) => {
    setFormats(prev => prev.includes(fmt) ? (prev.length > 1 ? prev.filter(f => f !== fmt) : prev) : [...prev, fmt]);
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (email && email.includes('@') && !recipients.includes(email)) {
      setRecipients(prev => [...prev, email]);
      setRecipientInput('');
    }
  };

  const handleSave = () => {
    if (!name.trim() || recipients.length === 0) return;
    const newJob: ScheduledJob = {
      id: `SCHED-${String(Math.floor(Math.random() * 900) + 100)}`,
      name: name.trim(),
      reportType,
      formats,
      frequency,
      dayOfWeek: frequency === 'weekly' ? dayOfWeek : undefined,
      dayOfMonth: frequency === 'monthly' || frequency === 'quarterly' ? dayOfMonth : undefined,
      time,
      recipients,
      status: 'active',
      createdBy: 'Current User',
      createdAt: '2026-08-21',
      nextRun: `2026-08-22 ${time}`,
    };
    onSave(newJob);
    onClose();
  };

  const allFormats: ExportFormat[] = ['pdf', 'excel', 'csv', 'xml'];
  const reportTypes = ['Investor Register', 'KYC / AML Status', 'Campaign Performance', 'FATCA / CRS XML'];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface-2 border border-border rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Repeat size={15} className="text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground text-sm">Create Scheduled Export Job</h2>
              <p className="text-xs text-muted-foreground">Configure recurring report delivery</p>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg"><X size={16} /></button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Job Name */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Job Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Weekly KYC Status Report"
              className="w-full px-3 py-2 text-sm bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {/* Report Type */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Report Type</label>
            <div className="relative">
              <select
                value={reportType}
                onChange={e => setReportType(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground appearance-none pr-8"
              >
                {reportTypes.map(rt => <option key={rt} value={rt}>{rt}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          {/* File Formats */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">File Formats</label>
            <div className="flex gap-2 flex-wrap">
              {allFormats.map(fmt => (
                <button
                  key={fmt}
                  onClick={() => toggleFormat(fmt)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                    formats.includes(fmt)
                      ? fmt === 'pdf' ? 'border-critical bg-critical/10 text-critical' :
                        fmt === 'excel' ? 'border-positive bg-positive/10 text-positive' :
                        fmt === 'csv'? 'border-info bg-info/10 text-info' : 'border-accent bg-accent/10 text-accent' :'border-border bg-surface-3 text-muted-foreground hover:border-border/80'
                  }`}
                >
                  {React.createElement(formatIcons[fmt], { size: 11 })}
                  {fmt.toUpperCase()}
                  {formats.includes(fmt) && <CheckSquare size={10} />}
                </button>
              ))}
            </div>
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Frequency</label>
            <div className="grid grid-cols-4 gap-1.5">
              {(['daily', 'weekly', 'monthly', 'quarterly'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFrequency(f)}
                  className={`py-2 rounded-lg text-xs font-semibold border capitalize transition-all ${
                    frequency === f ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-surface-3 border-border text-muted-foreground hover:border-border/80'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Day/Time */}
          <div className="grid grid-cols-2 gap-3">
            {frequency === 'weekly' && (
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Day of Week</label>
                <div className="relative">
                  <select
                    value={dayOfWeek}
                    onChange={e => setDayOfWeek(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground appearance-none pr-8"
                  >
                    {days.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
            )}
            {(frequency === 'monthly' || frequency === 'quarterly') && (
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Day of Month</label>
                <input
                  type="number"
                  min={1}
                  max={28}
                  value={dayOfMonth}
                  onChange={e => setDayOfMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground"
                />
              </div>
            )}
            <div className={frequency === 'daily' ? 'col-span-2' : ''}>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Run Time (UTC)</label>
              <input
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground"
              />
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Recipients</label>
            <div className="flex gap-2 mb-2">
              <input
                type="email"
                value={recipientInput}
                onChange={e => setRecipientInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addRecipient()}
                placeholder="email@example.com"
                className="flex-1 px-3 py-2 text-sm bg-surface-3 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground placeholder:text-muted-foreground"
              />
              <button onClick={addRecipient} className="btn-secondary text-xs px-3 gap-1"><Plus size={12} /> Add</button>
            </div>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {recipients.map(r => (
                  <span key={r} className="flex items-center gap-1 text-xs bg-surface-3 border border-border rounded-full px-2.5 py-1 text-foreground">
                    <Mail size={10} className="text-muted-foreground" />{r}
                    <button onClick={() => setRecipients(prev => prev.filter(x => x !== r))} className="text-muted-foreground hover:text-critical ml-0.5"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            {recipients.length === 0 && (
              <p className="text-xs text-muted-foreground">Add at least one recipient email address.</p>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-border flex-shrink-0 gap-3">
          <button onClick={onClose} className="btn-secondary text-sm flex-1">Cancel</button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || recipients.length === 0}
            className="btn-primary text-sm flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Play size={13} /> Create Job
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ComplianceReportingSuite() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('investor-register');
  const [searchQuery, setSearchQuery] = useState('');
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [scheduledJobs, setScheduledJobs] = useState<ScheduledJob[]>(initialScheduledJobs);
  const [executionHistory] = useState<JobExecution[]>(initialExecutionHistory);
  const [historyFilter, setHistoryFilter] = useState<'all' | 'completed' | 'failed'>('all');

  const handleExport = (reportType: string, format: ExportFormat) => {
    setExportingFormat(`${reportType}-${format}`);
    setTimeout(() => setExportingFormat(null), 1500);
  };

  const handleAddJob = (job: ScheduledJob) => {
    setScheduledJobs(prev => [job, ...prev]);
  };

  const toggleJobStatus = (id: string) => {
    setScheduledJobs(prev => prev.map(j => j.id === id ? { ...j, status: j.status === 'active' ? 'paused' : 'active' } : j));
  };

  const deleteJob = (id: string) => {
    setScheduledJobs(prev => prev.filter(j => j.id !== id));
  };

  const filteredHistory = executionHistory.filter(e => historyFilter === 'all' || e.status === historyFilter);

  const tabs: { key: ActiveTab; label: string; icon: React.ElementType }[] = [
    { key: 'investor-register', label: 'Investor Register', icon: Users },
    { key: 'kyc-aml', label: 'KYC / AML Status', icon: Shield },
    { key: 'campaign-performance', label: 'Campaign Performance', icon: BarChart3 },
    { key: 'fatca-crs', label: 'FATCA / CRS XML', icon: Globe },
    { key: 'scheduled-exports', label: 'Scheduled Exports', icon: Repeat },
  ];

  const activeJobs = scheduledJobs.filter(j => j.status === 'active').length;
  const completedRuns = executionHistory.filter(e => e.status === 'completed').length;
  const failedRuns = executionHistory.filter(e => e.status === 'failed').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Compliance Reporting & Export Suite</h1>
          <p className="text-sm text-muted-foreground mt-1">Regulatory reporting · Audit-ready exports · FATCA/CRS filing · 20 Aug 2026</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs gap-1.5"><Archive size={12} /> Export History</button>
          <button className="btn-secondary text-xs gap-1.5"><RefreshCw size={12} /> Refresh Data</button>
          <button className="btn-primary text-xs gap-1.5" onClick={() => { setActiveTab('scheduled-exports'); setShowCreateModal(true); }}>
            <Plus size={12} /> Schedule Export
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Total Investors" value="3,847" subValue="Registered & active" variant="default" icon={Users} iconColor="text-primary" />
        <MetricCard label="KYC Approved" value="3,621" subValue="94.1% approval rate" variant="positive" icon={CheckCircle} iconColor="text-positive" />
        <MetricCard label="FATCA/CRS Filed" value="3,241" subValue="FY2025 submissions" variant="info" icon={Globe} iconColor="text-info" />
        <MetricCard label="Pending EDD" value="12" subValue="Awaiting enhanced due diligence" variant="critical" icon={AlertTriangle} iconColor="text-critical" />
      </div>

      {/* Recent Exports */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <SectionHeader title="Recent Export Jobs" subtitle="Last 24 hours · Audit trail preserved" />
          <button className="text-xs text-primary hover:underline flex items-center gap-1"><Eye size={11} /> View All</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-3">
              <tr>
                {['Export ID', 'Report Type', 'Format', 'Requested By', 'Time', 'Records', 'Size', 'Status', 'Action'].map((h) => (
                  <th key={`th-exp-${h}`} className="table-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentExports.map((job) => {
                const FmtIcon = formatIcons[job.format];
                return (
                  <tr key={job.id} className="data-table-row">
                    <td className="table-td font-mono-data text-xs text-muted-foreground">{job.id}</td>
                    <td className="table-td font-medium text-foreground text-sm">{job.reportType}</td>
                    <td className="table-td">
                      <span className={`flex items-center gap-1 text-xs font-semibold ${formatColors[job.format]}`}>
                        <FmtIcon size={11} />{job.format.toUpperCase()}
                      </span>
                    </td>
                    <td className="table-td text-xs text-muted-foreground">{job.requestedBy}</td>
                    <td className="table-td text-xs font-mono-data text-muted-foreground">{job.requestedAt}</td>
                    <td className="table-td font-mono-data text-sm text-foreground">{job.recordCount?.toLocaleString()}</td>
                    <td className="table-td font-mono-data text-xs text-muted-foreground">{job.fileSize}</td>
                    <td className="table-td">
                      <StatusBadge status={job.status === 'completed' ? 'approved' : job.status === 'processing' ? 'pending' : 'rejected'} label={job.status} />
                    </td>
                    <td className="table-td">
                      <button className="btn-ghost text-xs gap-1"><Download size={11} /> Download</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={`report-tab-${key}`}
            onClick={() => setActiveTab(key)}
            className={`portal-tab flex items-center gap-1.5 ${activeTab === key ? 'active' : ''}`}
          >
            <Icon size={13} />{label}
            {key === 'scheduled-exports' && activeJobs > 0 && (
              <span className="bg-primary/20 text-primary text-xs font-mono-data px-1.5 py-0.5 rounded-full">{activeJobs}</span>
            )}
          </button>
        ))}
      </div>

      {/* Search + Export Controls (not shown on scheduled-exports tab) */}
      {activeTab !== 'scheduled-exports' && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search records..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-surface-2 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/40 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <button className="btn-secondary text-xs gap-1.5"><Filter size={12} /> Filters</button>
          <button className="btn-secondary text-xs gap-1.5"><Calendar size={12} /> Date Range</button>
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-xs text-muted-foreground font-medium">Export as:</span>
            {(['pdf', 'excel', 'csv'] as ExportFormat[]).map((fmt) => (
              <ExportButton
                key={fmt}
                format={fmt}
                onClick={() => handleExport(activeTab, fmt)}
              />
            ))}
            {activeTab === 'fatca-crs' && (
              <ExportButton format="xml" onClick={() => handleExport(activeTab, 'xml')} />
            )}
            {exportingFormat && (
              <span className="text-xs text-positive flex items-center gap-1 animate-pulse">
                <CheckCircle size={11} /> Generating...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Investor Register Tab */}
      {activeTab === 'investor-register' && (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <SectionHeader title="Investor Register" subtitle={`${investorRegisterData.length} records shown · Full register: 3,847 investors`} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-3">
                <tr>
                  {['Investor ID', 'Name', 'Type', 'Jurisdiction', 'Tax ID', 'AUM', 'KYC Status', 'FATCA', 'CRS', 'Invested'].map((h) => (
                    <th key={`th-inv-${h}`} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {investorRegisterData
                  .filter((r) => !searchQuery || r.name.toLowerCase().includes(searchQuery.toLowerCase()) || r.id.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((inv) => (
                    <tr key={inv.id} className="data-table-row">
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{inv.id}</td>
                      <td className="table-td">
                        <p className="font-medium text-foreground text-sm">{inv.name}</p>
                        <p className="text-xs text-muted-foreground">{inv.accreditation === 'Verified' ? '✓ Accredited' : '⏳ Pending'}</p>
                      </td>
                      <td className="table-td text-xs text-muted-foreground">{inv.type}</td>
                      <td className="table-td text-xs text-muted-foreground">{inv.jurisdiction}</td>
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{inv.taxId}</td>
                      <td className="table-td font-mono-data font-semibold text-foreground text-sm">{inv.aum}</td>
                      <td className="table-td">
                        <StatusBadge
                          status={inv.kycStatus === 'Approved' ? 'approved' : inv.kycStatus === 'EDD Required' ? 'flagged' : 'pending'}
                          label={inv.kycStatus}
                        />
                      </td>
                      <td className="table-td">
                        <span className={`text-xs font-semibold ${inv.fatcaStatus === 'Compliant' ? 'text-positive' : inv.fatcaStatus === 'Exempt' ? 'text-info' : 'text-warning'}`}>
                          {inv.fatcaStatus}
                        </span>
                      </td>
                      <td className="table-td">
                        <span className={`text-xs font-semibold ${inv.crsStatus === 'Reported' ? 'text-positive' : inv.crsStatus === 'Exempt' ? 'text-info' : 'text-warning'}`}>
                          {inv.crsStatus}
                        </span>
                      </td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{inv.investedDate}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* KYC/AML Status Tab */}
      {activeTab === 'kyc-aml' && (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <SectionHeader title="KYC / AML Status Report" subtitle="Compliance screening status for all investors" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-3">
                <tr>
                  {['Case ID', 'Investor', 'Type', 'Status', 'Risk Score', 'AML Flags', 'PEP', 'Sanctions', 'EDD', 'Reviewer', 'Next Review'].map((h) => (
                    <th key={`th-kyc-${h}`} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {kycAmlData
                  .filter((r) => !searchQuery || r.investor.toLowerCase().includes(searchQuery.toLowerCase()) || r.id.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((kyc) => (
                    <tr key={kyc.id} className="data-table-row">
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{kyc.id}</td>
                      <td className="table-td">
                        <p className="font-medium text-foreground text-sm">{kyc.investor}</p>
                        <p className="text-xs text-muted-foreground">{kyc.type}</p>
                      </td>
                      <td className="table-td text-xs text-muted-foreground">{kyc.type}</td>
                      <td className="table-td">
                        <StatusBadge
                          status={kyc.status === 'Approved' ? 'approved' : kyc.status === 'EDD Required' ? 'flagged' : 'pending'}
                          label={kyc.status}
                        />
                      </td>
                      <td className="table-td">
                        <div className="flex items-center gap-2">
                          <span className="font-mono-data font-bold text-sm text-foreground">{kyc.riskScore}</span>
                          <RiskBandPill band={kyc.riskBand} />
                        </div>
                      </td>
                      <td className="table-td">
                        <span className={`font-mono-data font-bold text-sm ${kyc.amlFlags > 0 ? 'text-critical' : 'text-positive'}`}>
                          {kyc.amlFlags}
                        </span>
                      </td>
                      <td className="table-td">
                        <span className={`text-xs font-semibold ${kyc.pepMatch ? 'text-critical' : 'text-positive'}`}>
                          {kyc.pepMatch ? '⚠ Match' : '✓ Clear'}
                        </span>
                      </td>
                      <td className="table-td">
                        <span className={`text-xs font-semibold ${kyc.sanctionsMatch ? 'text-critical' : 'text-positive'}`}>
                          {kyc.sanctionsMatch ? '⚠ Match' : '✓ Clear'}
                        </span>
                      </td>
                      <td className="table-td">
                        <span className={`text-xs font-semibold ${kyc.eddRequired ? 'text-warning' : 'text-muted-foreground'}`}>
                          {kyc.eddRequired ? 'Required' : 'N/A'}
                        </span>
                      </td>
                      <td className="table-td text-xs text-muted-foreground">{kyc.reviewer}</td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{kyc.nextReview ?? '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Campaign Performance Tab */}
      {activeTab === 'campaign-performance' && (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <SectionHeader title="Campaign Performance Report" subtitle="All campaigns across all tenants · Audit-ready" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-3">
                <tr>
                  {['Campaign ID', 'Name', 'Issuer', 'Type', 'Target', 'Raised', 'Completion', 'Investors', 'Status', 'IRR', 'Distributions'].map((h) => (
                    <th key={`th-camp-${h}`} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campaignPerformanceData
                  .filter((r) => !searchQuery || r.name.toLowerCase().includes(searchQuery.toLowerCase()) || r.id.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((camp) => (
                    <tr key={camp.id} className="data-table-row">
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{camp.id}</td>
                      <td className="table-td">
                        <p className="font-medium text-foreground text-sm">{camp.name}</p>
                        <p className="text-xs text-muted-foreground">{camp.launchDate ?? 'Not launched'}</p>
                      </td>
                      <td className="table-td text-xs text-muted-foreground">{camp.issuer}</td>
                      <td className="table-td text-xs text-muted-foreground">{camp.type}</td>
                      <td className="table-td font-mono-data text-sm text-foreground">{camp.targetRaise}</td>
                      <td className="table-td font-mono-data font-semibold text-foreground text-sm">{camp.raisedAmount}</td>
                      <td className="table-td">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-surface-4 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${camp.completion >= 100 ? 'bg-positive' : camp.completion >= 50 ? 'bg-info' : 'bg-warning'}`}
                              style={{ width: `${Math.min(camp.completion, 100)}%` }}
                            />
                          </div>
                          <span className="font-mono-data text-xs text-foreground">{camp.completion}%</span>
                        </div>
                      </td>
                      <td className="table-td font-mono-data text-sm text-foreground">{camp.investors}</td>
                      <td className="table-td">
                        <StatusBadge
                          status={camp.status === 'Funded' || camp.status === 'Closed' ? 'approved' : camp.status === 'Compliance Hold' ? 'flagged' : 'pending'}
                          label={camp.status}
                        />
                      </td>
                      <td className="table-td font-mono-data font-semibold text-sm text-foreground">{camp.irr}</td>
                      <td className="table-td font-mono-data text-sm text-foreground">{camp.distributions}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FATCA/CRS Tab */}
      {activeTab === 'fatca-crs' && (
        <div className="space-y-4">
          <div className="alert-banner" style={{ background: 'var(--color-info, #3b82f6)10', borderColor: 'var(--color-info, #3b82f6)30' }}>
            <Globe size={16} className="flex-shrink-0 text-info" />
            <div>
              <p className="font-semibold text-sm text-foreground">FATCA/CRS XML Filing — FY2025 Reporting Period</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                3,241 accounts reported · 312 exempt · 294 pending · Filing deadline: 30 Jun 2026 · OECD CRS Schema v2.0 · IRS FATCA Schema v2.0
              </p>
            </div>
            <button className="ml-auto flex-shrink-0 btn-primary text-xs gap-1.5">
              <Download size={11} /> Generate XML Bundle
            </button>
          </div>

          <div className="glass-card rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <SectionHeader title="FATCA / CRS Account Records" subtitle="Reportable accounts · XML schema compliant" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-3">
                  <tr>
                    {['Record ID', 'Account Holder', 'TIN', 'Country', 'Account Type', 'Balance', 'FATCA', 'CRS', 'Filing Date', 'XML'].map((h) => (
                      <th key={`th-fatca-${h}`} className="table-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fatcaCrsData
                    .filter((r) => !searchQuery || r.investor.toLowerCase().includes(searchQuery.toLowerCase()) || r.id.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((rec) => (
                      <tr key={rec.id} className="data-table-row">
                        <td className="table-td font-mono-data text-xs text-muted-foreground">{rec.id}</td>
                        <td className="table-td font-medium text-foreground text-sm">{rec.investor}</td>
                        <td className="table-td font-mono-data text-xs text-muted-foreground">{rec.tin}</td>
                        <td className="table-td text-xs text-muted-foreground">{rec.country}</td>
                        <td className="table-td text-xs text-muted-foreground">{rec.accountType}</td>
                        <td className="table-td font-mono-data font-semibold text-foreground text-sm">{rec.balance}</td>
                        <td className="table-td">
                          <span className={`text-xs font-semibold ${rec.fatcaStatus === 'Compliant' ? 'text-positive' : rec.fatcaStatus === 'Exempt' ? 'text-info' : 'text-warning'}`}>
                            {rec.fatcaStatus}
                          </span>
                        </td>
                        <td className="table-td">
                          <span className={`text-xs font-semibold ${rec.crsStatus === 'Reported' ? 'text-positive' : rec.crsStatus === 'Exempt' ? 'text-info' : 'text-warning'}`}>
                            {rec.crsStatus}
                          </span>
                        </td>
                        <td className="table-td text-xs font-mono-data text-muted-foreground">{rec.filingDate ?? '—'}</td>
                        <td className="table-td">
                          {rec.xmlGenerated ? (
                            <button className="btn-ghost text-xs gap-1 text-positive"><Download size={11} /> XML</button>
                          ) : (
                            <button className="btn-ghost text-xs gap-1 text-warning"><Clock size={11} /> Pending</button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="glass-card rounded-xl p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">FATCA XML Schema</p>
              <div className="space-y-2 text-xs">
                {[
                  ['Schema Version', 'IRS FATCA v2.0'],
                  ['Namespace', 'urn:us:gov:treasury:irs:ext:fatca:...'],
                  ['Reporting FI', 'CrowdVault Platform Ltd.'],
                  ['GIIN', 'ABCDE1.00000.LE.826'],
                  ['Reporting Period', 'FY2025 (Jan–Dec 2025)'],
                  ['Submission Method', 'IDES (IRS Data Exchange)'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-mono-data text-foreground font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">CRS XML Schema</p>
              <div className="space-y-2 text-xs">
                {[
                  ['Schema Version', 'OECD CRS v2.0'],
                  ['Namespace', 'urn:oecd:ties:crs:v2'],
                  ['Reporting Entity', 'CrowdVault Platform Ltd.'],
                  ['Jurisdiction', 'United Kingdom'],
                  ['Reporting Period', 'FY2025 (Jan–Dec 2025)'],
                  ['Submission Method', 'HMRC AEOI Portal'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-mono-data text-foreground font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled Exports Tab */}
      {activeTab === 'scheduled-exports' && (
        <div className="space-y-5">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="glass-card rounded-xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Repeat size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground">{activeJobs}</p>
                <p className="text-xs text-muted-foreground">Active Jobs</p>
              </div>
            </div>
            <div className="glass-card rounded-xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-positive/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle size={16} className="text-positive" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground">{completedRuns}</p>
                <p className="text-xs text-muted-foreground">Completed Runs</p>
              </div>
            </div>
            <div className="glass-card rounded-xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-critical/10 flex items-center justify-center flex-shrink-0">
                <XCircle size={16} className="text-critical" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground">{failedRuns}</p>
                <p className="text-xs text-muted-foreground">Failed Runs</p>
              </div>
            </div>
          </div>

          {/* Scheduled Jobs List */}
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <SectionHeader title="Scheduled Export Jobs" subtitle={`${scheduledJobs.length} jobs configured`} />
              <button onClick={() => setShowCreateModal(true)} className="btn-primary text-xs gap-1.5">
                <Plus size={12} /> New Job
              </button>
            </div>
            <div className="divide-y divide-border">
              {scheduledJobs.map(job => (
                <div key={job.id} className="px-4 py-3.5 hover:bg-surface-3/50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${job.status === 'active' ? 'bg-positive/10' : 'bg-surface-4'}`}>
                        <Repeat size={14} className={job.status === 'active' ? 'text-positive' : 'text-muted-foreground'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-foreground">{job.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${job.status === 'active' ? 'bg-positive/10 text-positive border-positive/20' : 'bg-surface-4 text-muted-foreground border-border'}`}>
                            {job.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground">{job.reportType}</span>
                          <span className="text-xs text-muted-foreground">·</span>
                          <span className="text-xs text-muted-foreground capitalize">{job.frequency} at {job.time} UTC</span>
                          {job.dayOfWeek && <><span className="text-xs text-muted-foreground">·</span><span className="text-xs text-muted-foreground">{job.dayOfWeek}s</span></>}
                          {job.dayOfMonth && <><span className="text-xs text-muted-foreground">·</span><span className="text-xs text-muted-foreground">Day {job.dayOfMonth}</span></>}
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {job.formats.map(f => <FormatPill key={f} format={f} />)}
                          <span className="text-xs text-muted-foreground ml-1"><Mail size={10} className="inline mr-0.5" />{job.recipients.length} recipient{job.recipients.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                          {job.lastRun && <span><Clock size={10} className="inline mr-1" />Last: {job.lastRun}</span>}
                          <span><Timer size={10} className="inline mr-1" />Next: {job.nextRun}</span>
                          <span>By {job.createdBy}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => toggleJobStatus(job.id)}
                        className={`btn-ghost text-xs gap-1 ${job.status === 'active' ? 'text-warning' : 'text-positive'}`}
                        title={job.status === 'active' ? 'Pause job' : 'Resume job'}
                      >
                        {job.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                        {job.status === 'active' ? 'Pause' : 'Resume'}
                      </button>
                      <button onClick={() => deleteJob(job.id)} className="btn-ghost text-xs gap-1 text-critical" title="Delete job">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {scheduledJobs.length === 0 && (
                <div className="px-4 py-10 text-center">
                  <Repeat size={28} className="text-muted-foreground mx-auto mb-2 opacity-40" />
                  <p className="text-sm text-muted-foreground">No scheduled jobs yet.</p>
                  <button onClick={() => setShowCreateModal(true)} className="btn-primary text-xs mt-3 gap-1.5"><Plus size={12} /> Create First Job</button>
                </div>
              )}
            </div>
          </div>

          {/* Execution History */}
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
              <SectionHeader title="Job Execution History" subtitle="All scheduled job runs with status" />
              <div className="flex items-center gap-1.5">
                {(['all', 'completed', 'failed'] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setHistoryFilter(f)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold capitalize transition-all ${historyFilter === f ? 'bg-primary/10 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground border border-transparent'}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-3">
                  <tr>
                    {['Execution ID', 'Job Name', 'Report Type', 'Formats', 'Started', 'Completed', 'Duration', 'Records', 'Recipients', 'Status'].map(h => (
                      <th key={`th-exec-${h}`} className="table-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map(exec => (
                    <tr key={exec.id} className="data-table-row">
                      <td className="table-td font-mono-data text-xs text-muted-foreground">{exec.id}</td>
                      <td className="table-td">
                        <p className="font-medium text-foreground text-sm">{exec.jobName}</p>
                        <p className="text-xs text-muted-foreground font-mono-data">{exec.jobId}</p>
                      </td>
                      <td className="table-td text-xs text-muted-foreground">{exec.reportType}</td>
                      <td className="table-td">
                        <div className="flex gap-1 flex-wrap">
                          {exec.formats.map(f => <FormatPill key={f} format={f} />)}
                        </div>
                      </td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{exec.startedAt}</td>
                      <td className="table-td text-xs font-mono-data text-muted-foreground">{exec.completedAt ?? '—'}</td>
                      <td className="table-td text-xs font-mono-data text-foreground">{exec.duration ?? '—'}</td>
                      <td className="table-td font-mono-data text-sm text-foreground">{exec.recordCount?.toLocaleString() ?? '—'}</td>
                      <td className="table-td font-mono-data text-sm text-foreground">{exec.recipients}</td>
                      <td className="table-td">
                        <div>
                          <StatusBadge
                            status={exec.status === 'completed' ? 'approved' : exec.status === 'running' ? 'pending' : 'rejected'}
                            label={exec.status}
                          />
                          {exec.errorMessage && (
                            <p className="text-xs text-critical mt-1 max-w-[200px] leading-tight">{exec.errorMessage}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Create Job Modal */}
      {showCreateModal && (
        <CreateJobModal onClose={() => setShowCreateModal(false)} onSave={handleAddJob} />
      )}
    </div>
  );
}
